import type { CSSProperties } from "react";
import { LissenGlyph } from "./logo";
import { lissenDress, tokens } from "./tokens";

/**
 * A settled address, as a chip. Quiet when the visitor typed it; dressed as
 * the app icon, with the glyph, when Lissen recognised them. The × is on the
 * chip, as a mail client draws it, and means whatever the host says it
 * means — "not this address" at a checkout, "forget this device" on a page
 * with a session.
 */
export function EmailChip({
  email,
  lissen = false,
  onRemove,
  removeLabel = "Remove email",
  disabled = false,
  style,
}: {
  email: string;
  /** The address came from a Lissen identity, not from typing. */
  lissen?: boolean;
  onRemove?: () => void;
  removeLabel?: string;
  disabled?: boolean;
  style?: CSSProperties;
}) {
  return (
    <span
      style={{
        alignItems: "center",
        borderRadius: `calc(${tokens.radiusSmall} * 1.6)`,
        display: "inline-flex",
        fontSize: tokens.fontSizeSmall,
        gap: `calc(${tokens.space} * 0.75)`,
        lineHeight: 1,
        maxWidth: "100%",
        // Shrinks with its row: a long address ellipsises rather than
        // pushing whatever sits beside it off the screen.
        minWidth: 0,
        padding: `calc(${tokens.space} * 0.5) calc(${tokens.space} * 0.5) calc(${tokens.space} * 0.5) calc(${tokens.space} * 0.75)`,
        ...(lissen
          ? lissenDress
          : {
              background: `color-mix(in srgb, ${tokens.text} 7%, transparent)`,
              border: `${tokens.borderWidth} solid ${tokens.border}`,
              color: tokens.text,
            }),
        ...style,
      }}
    >
      {lissen ? <LissenGlyph height="1.1em" /> : null}
      <span
        style={{
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {email}
      </span>
      {onRemove ? (
        <button
          type="button"
          aria-label={removeLabel}
          disabled={disabled}
          onClick={onRemove}
          style={{
            alignItems: "center",
            appearance: "none",
            background: "none",
            border: 0,
            borderRadius: tokens.radiusSmall,
            color: "inherit",
            cursor: disabled ? "default" : "pointer",
            display: "inline-flex",
            font: "inherit",
            height: "1.5em",
            justifyContent: "center",
            opacity: disabled ? 0.5 : 1,
            padding: 0,
            width: "1.5em",
          }}
        >
          <svg
            viewBox="0 0 12 12"
            width="0.75em"
            height="0.75em"
            aria-hidden="true"
          >
            <path
              d="M2 2l8 8M10 2l-8 8"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </button>
      ) : null}
    </span>
  );
}
